import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography, Stack, Grid, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Chip, Avatar, TablePagination, Dialog, DialogContent, DialogTitle, DialogActions, MenuItem, Menu, Drawer, useMediaQuery, Divider, ListItemIcon, ListItemText, Card, CardContent, TextField, FormControl, InputLabel, Select, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TrendingUp, Assignment, Timeline, Folder, MoreVert, Close as CloseIcon, Edit, Delete, Visibility, GroupAdd, PostAdd, AssignmentInd, AddTask, AutoAwesomeMotion, Search as SearchIcon, RestartAlt, FolderOpenOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { getData, deleteData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import Loader from '../../../common/spinner';
import { getStatusColor, priorityColors } from '../../../utils/statusColor';
import CreateProject from './createProject';
import EditProjectPage from './updateProject';
import ProjectViewPage from './projectOverview';
import projectIcon from "../../../assets/icons/Project-Icon.svg";
import AssignProjectTeam from './assignProjectTeam';
import CreateEpicPage from '../EpicManagement/createEpic';
import CreateUserStory from '../EpicManagement/userStoryManagement/createStory';
import CreateTask from '../EpicManagement/taskManagement/createTask';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ title, value, icon, color }) => (
    <Card
        sx={{
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            height: '100%',
            // Using your dynamic color for the gradient
            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
            color: '#FFFFFF',
            position: 'relative',
            overflow: 'hidden',
            border: 'none'
        }}
    >
        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '600',
                            opacity: 0.9
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5 }}>
                        {value}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        p: 1.5,
                        borderRadius: '12px',
                        display: 'flex',
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    {React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
                </Box>
            </Stack>
        </CardContent>
        <Box
            sx={{
                position: 'absolute',
                top: '-20%',
                right: '-10%',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                zIndex: 0
            }}
        />
    </Card>
);

const ProjectsPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [projectData, setProjectData] = useState([]);
    const [statsCount, setStatsCounts] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [state, setState] = useState({
        isMyView: false,
        page: 0,
        limit: 10,
        totalCount: 0,
        isModalOpen: false,
        modalType: null,
        selectedProject: null,
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            // Reset to page 0 when searching
            updateState({ page: 0 });
        }, 500); // Wait 500ms after user stops typing
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const userRole = userData?.user_type;
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);

    const [statusFilter, setStatusFilter] = useState('');
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';

    const updateState = (newState) => {
        setState((prev) => ({ ...prev, ...newState }));
    };

    const fetchProjects = useCallback(async () => {
        if (!userId) return;

        setIsLoading(true);
        try {
            const baseUrl = state.isMyView
                ? API_ENDPOINTS.fetchEmployeeProjects
                : API_ENDPOINTS.fetchSupervisorProjects;

            const apiPage = state.page + 1;

            let url = `${baseUrl}/${userId}?page=${apiPage}&limit=${state.limit}`;

            if (debouncedSearch) {
                url += `&search=${encodeURIComponent(debouncedSearch)}`;
            }
            if (statusFilter) {
                url += `&status=${encodeURIComponent(statusFilter)}`;
            }

            const response = await getData(url);

            if (response.status === "SUCCESS") {
                setStatsCounts(response.message || {});
                setProjectData(response.message.data || []);
                updateState({ totalCount: parseInt(response.message.total || 0, 10) });
            }
        } catch (error) {
            setProjectData([]);
        } finally {
            setTimeout(() => setIsLoading(false), 300);
        }
    }, [state.isMyView, state.page, state.limit, userId, debouncedSearch, statusFilter]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const displayData = projectData;

    const handleCloseModal = (event, reason, refresh = false) => {
        // If the user clicked outside (backdrop), do nothing
        if (reason && reason === "backdropClick") {
            return;
        }

        updateState({ isModalOpen: false });
        if (refresh === true) {
            fetchProjects();
        }
    };

    const handleOpenMenu = (event, project) => {
        setAnchorEl(event.currentTarget);
        setSelectedProject(project);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedProject(null);
    };

    const handleAction = (type, project) => {
        setState(prev => ({
            ...prev,
            modalType: type,
            selectedProject: project,
            isModalOpen: true
        }));
        handleCloseMenu();
    };

    const handleDeleteSubmit = async (projectId) => {
        setIsLoading(true);
        try {
            const url = API_ENDPOINTS.deleteProjectById(projectId);
            const response = await deleteData(url);

            if (response.status === "SUCCESS") {
                handleCloseModal(undefined, undefined, true);
            }
        } catch (error) {
            return;
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setSearchTerm('');
        setStatusFilter('');
        updateState({ page: 0 });
    };

    const ProjectMobileCard = ({ proj }) => (
        <Card sx={{ borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar sx={{ borderRadius: '10px', bgcolor: '#f1f5f9', color: '#3b82f6', width: 40, height: 40, flexShrink: 0 }}>
                            {proj.project_title ? proj.project_title[0].toUpperCase() : 'P'}
                        </Avatar>
                        <Typography sx={{ fontWeight: 700, color: '#1E293B', wordBreak: 'break-word' }}>
                            {proj.project_title}
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, proj)} sx={{ flexShrink: 0 }}>
                        <MoreVert fontSize="small" />
                    </IconButton>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        label={proj.status}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            bgcolor: getStatusColor(proj.status).bg,
                            color: getStatusColor(proj.status).color,
                            border: `1px solid ${getStatusColor(proj.status).border}`
                        }}
                    />
                    <Chip
                        label={proj.priority}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            bgcolor: priorityColors[proj.priority]?.bg || '#f1f5f9',
                            color: priorityColors[proj.priority]?.color || '#475569',
                            border: `1px solid ${priorityColors[proj.priority]?.border || '#cbd5e1'}`
                        }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                        {`${Math.round(proj.progress || 0)}% complete`}
                    </Typography>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }} color="text.secondary">Manager</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>
                                {proj.manager?.first_name?.[0]?.toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                                {proj.manager ? `${proj.manager.first_name} ${proj.manager.last_name}` : 'Unassigned'}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }} color="text.secondary">Team Lead</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>
                                {proj.team_leader?.first_name?.[0]?.toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                                {proj.team_leader ? `${proj.team_leader.first_name} ${proj.team_leader.last_name}` : 'Unassigned'}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }} color="text.secondary">Client</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', textAlign: 'right' }}>
                            {proj.clients && proj.clients.length > 0
                                ? proj.clients.map(client => `${client.first_name} ${client.last_name || ''}`.trim()).join(', ')
                                : 'No Client'}
                        </Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Loader />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" mt={{ xs: 1.5, sm: 0 }} mb={3} alignItems="center" flexWrap="wrap" gap={2}>
                <Typography variant="h5" fontWeight="700">
                    Projects Overview
                </Typography>

                <Stack direction="row" spacing={3} alignItems="center">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAction('create', { isModalOpen: true })}
                        sx={{
                            borderRadius: '8px',
                            bgcolor: '#6366f1',
                            textTransform: 'none',
                            px: { xs: 1.5, sm: 3 },
                            '&:hover': { bgcolor: '#4f46e5' }
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New Project</Box>
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={3} mb={4}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="All Projects"
                        value={statsCount.total_project_count || 0}
                        icon={<TrendingUp />}
                        color="#6366F1"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Todo"
                        value={statsCount.todo_project_count || 0}
                        icon={<Assignment />}
                        color="#F59E0B"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="In Progress"
                        value={statsCount.inprogress_count || 0}
                        icon={<Timeline />}
                        color="#3B82F6"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Overdue"
                        value={statsCount.overdue_count || 0}
                        icon={<Folder />}
                        color="#EF4444"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center" mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Search Project, Manager, Lead or Client..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} fontSize="small" />,
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value=""><em>All Status</em></MenuItem>
                            <MenuItem value="Todo">Todo</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Completed">Completed</MenuItem>
                            <MenuItem value="Overdue">Overdue</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                    <Button fullWidth startIcon={<RestartAlt />} onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
            </Grid>

            <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {displayData.length > 0 ? displayData.map((proj) => (
                    <ProjectMobileCard key={proj.id} proj={proj} />
                )) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.6, py: 6 }}>
                        <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: '50%', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FolderOpenOutlined sx={{ fontSize: 48, color: '#6366f1' }} />
                        </Box>
                        <Typography variant="h6" fontWeight="600" color="text.primary">No Projects Found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mt: 1, textAlign: 'center' }}>
                            {searchTerm || statusFilter
                                ? "We couldn't find any projects matching your current filters. Try adjusting your search or status selection."
                                : "There are currently no projects listed in this view."}
                        </Typography>
                    </Box>
                )}
            </Stack>

            <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                {['Project', 'Manager', 'Team Lead', 'Client', 'Status', 'Priority', 'Progress', 'Actions'].map((h) => (
                                    <TableCell key={h} sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#fff' }}>
                            {displayData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.6 }}>
                                            {/* Icon with a subtle background circle */}
                                            <Box sx={{
                                                bgcolor: '#f8fafc',
                                                p: 2,
                                                borderRadius: '50%',
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <FolderOpenOutlined sx={{ fontSize: 48, color: '#6366f1' }} />
                                            </Box>

                                            {/* Main Message */}
                                            <Typography variant="h6" fontWeight="600" color="text.primary">
                                                No Projects Found
                                            </Typography>

                                            {/* Sub Message */}
                                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mt: 1 }}>
                                                {searchTerm || statusFilter
                                                    ? "We couldn't find any projects matching your current filters. Try adjusting your search or status selection."
                                                    : "There are currently no projects listed in this view."}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                displayData.map((proj) => (
                                    <TableRow key={proj.id} hover sx={{
                                        '&:hover': {
                                            bgcolor: '#F1F5F9 !important'
                                        }
                                    }}>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ borderRadius: '10px', bgcolor: '#f1f5f9', color: '#3b82f6', width: 40, height: 40 }}>
                                                    {proj.project_title ? proj.project_title[0].toUpperCase() : 'P'}
                                                </Avatar>
                                                <Typography fontWeight={600}>{proj.project_title}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
                                                    {proj.manager?.first_name?.[0].toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2">
                                                    {proj.manager ? `${proj.manager.first_name} ${proj.manager.last_name}` : 'Unassigned'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
                                                    {proj.team_leader?.first_name?.[0].toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2">
                                                    {proj.team_leader ? `${proj.team_leader.first_name} ${proj.team_leader.last_name}` : 'Unassigned'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Typography variant="body2">
                                                {proj.clients && proj.clients.length > 0
                                                    ? proj.clients
                                                        .map(client => `${client.first_name} ${client.last_name || ''}`.trim())
                                                        .join(', ')
                                                    : 'No Client'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={proj.status}
                                                size="small"
                                                sx={{
                                                    width: 90,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    bgcolor: getStatusColor(proj.status).bg,
                                                    color: getStatusColor(proj.status).color,
                                                    border: `1px solid ${getStatusColor(proj.status).border}`
                                                }}
                                            />
                                        </TableCell>

                                        {/* Priority */}
                                        <TableCell>
                                            <Chip
                                                label={proj.priority}
                                                size="small"
                                                sx={{
                                                    width: 80,
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    bgcolor: priorityColors[proj.priority]?.bg || '#f1f5f9',
                                                    color: priorityColors[proj.priority]?.color || '#475569',
                                                    border: `1px solid ${priorityColors[proj.priority]?.border || '#cbd5e1'}`
                                                }}
                                            />
                                        </TableCell>

                                        {/* UPDATED: Progress Column */}
                                        <TableCell sx={{ width: '80px', textAlign: 'center' }}>
                                            <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                                                {/* Background Circle */}
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={100}
                                                    size={42}
                                                    thickness={4}
                                                    sx={{ color: '#e2e8f0' }}
                                                />
                                                {/* Active Progress Circle */}
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={Math.min(proj.progress || 0, 100)}
                                                    size={42}
                                                    thickness={4}
                                                    sx={{
                                                        color: proj.progress >= 100 ? '#22c55e' : '#6366f1',
                                                        position: 'absolute',
                                                        left: 0,
                                                        [`& .MuiCircularProgress-circle`]: { strokeLinecap: 'round' },
                                                    }}
                                                />
                                                {/* Percentage Text Overlay */}
                                                <Box
                                                    sx={{
                                                        top: 0, left: 0, bottom: 0, right: 0,
                                                        position: 'absolute',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                                                        {`${Math.round(proj.progress || 0)}%`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1E293B' }} onClick={(e) => e.stopPropagation()}>
                                            <IconButton size="small" onClick={(e) => handleOpenMenu(e, proj)}>
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>
            <TablePagination
                component="div"
                count={state.totalCount}
                page={state.page}
                onPageChange={(_, newPage) => {
                    updateState({ page: newPage });
                }}
                rowsPerPage={state.limit}
                onRowsPerPageChange={(event) => {
                    updateState({
                        limit: parseInt(event.target.value, 10),
                        page: 0 // Reset to first page when limit changes
                    });
                }}
                rowsPerPageOptions={[5, 10, 25]}
            />

            {(() => {
                const menuItemsContent = (
                    <>
                        {/* VIEW SECTION */}
                        <MenuItem onClick={() => {
                            handleCloseMenu();
                            const pId = selectedProject?.id;
                            const pName = selectedProject?.project_title;
                            // Dynamically uses /team-lead or /manager
                            navigate(`${roleBasePath}/projects/edit?projectId=${pId}&projectTitle=${pName}`);
                        }}>
                            <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
                            <ListItemText>View Details</ListItemText>
                        </MenuItem>

                        <Divider />

                        {/* MANAGEMENT SECTION */}
                        <MenuItem onClick={() => handleAction('assign', selectedProject)}>
                            <ListItemIcon><GroupAdd fontSize="small" /></ListItemIcon>
                            <ListItemText>Assign Team</ListItemText>
                        </MenuItem>

                        <Divider />

                        {/* CREATE SECTION */}
                        <MenuItem onClick={() => handleAction('createEpic', selectedProject)}>
                            <ListItemIcon><PostAdd fontSize="small" color="primary" /></ListItemIcon>
                            <ListItemText>Create Epic</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleAction('createStory', selectedProject)}>
                            <ListItemIcon><PostAdd fontSize="small" color="primary" /></ListItemIcon>
                            <ListItemText>Create Story</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleAction('createTask', selectedProject)}>
                            <ListItemIcon><PostAdd fontSize="small" color="primary" /></ListItemIcon>
                            <ListItemText>Create Task</ListItemText>
                        </MenuItem>

                        <Divider />

                        <MenuItem onClick={() => {
                            handleCloseMenu();
                            const pId = selectedProject?.id;
                            const pName = selectedProject?.project_title;
                            navigate(`${roleBasePath}/epic-management?projectId=${pId}&projectTitle=${pName}`);
                        }}>
                            <ListItemIcon><AssignmentInd fontSize="small" /></ListItemIcon>
                            <ListItemText>View Epics</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            handleCloseMenu();
                            const pId = selectedProject?.id;
                            const pName = selectedProject?.project_title;
                            navigate(`${roleBasePath}/user-stories?projectId=${pId}&projectTitle=${pName}`);
                        }}>
                            <ListItemIcon><AssignmentInd fontSize="small" /></ListItemIcon>
                            <ListItemText>View Stories</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            handleCloseMenu();
                            const pId = selectedProject?.id;
                            const pName = selectedProject?.project_title;
                            navigate(`${roleBasePath}/user-tasks?projectId=${pId}&projectTitle=${pName}&origin=Project`);
                        }}>
                            <ListItemIcon><AssignmentInd fontSize="small" /></ListItemIcon>
                            <ListItemText>View Tasks</ListItemText>
                        </MenuItem>

                        <Divider />

                        <MenuItem onClick={() => handleAction('delete', selectedProject)} sx={{ color: 'error.main' }}>
                            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText>Delete Project</ListItemText>
                        </MenuItem>
                    </>
                );

                return isMobile ? (
                    <Drawer
                        anchor="bottom"
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenu}
                        PaperProps={{ sx: { borderTopLeftRadius: '16px', borderTopRightRadius: '16px', pb: 1 } }}
                    >
                        <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', mx: 'auto', mt: 1.5, mb: 0.5 }} />
                        {menuItemsContent}
                    </Drawer>
                ) : (
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenu}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        PaperProps={{ sx: { width: 220, borderRadius: '12px', mt: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
                    >
                        {menuItemsContent}
                    </Menu>
                );
            })()}

            <Dialog
                open={state.isModalOpen}
                onClose={(event, reason) => handleCloseModal(event, reason)}
                maxWidth={
                    state.modalType === 'delete'
                        ? 'sm'
                        : ['create', 'edit', 'view', 'assign', 'createEpic', 'createStory', 'createTask'].includes(state.modalType)
                            ? 'md'
                            : 'sm'
                }
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: { xs: 0, sm: '16px' },
                        m: { xs: 0, sm: 4 },
                        width: { xs: '100%', sm: 800 },
                        maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
                        p: state.modalType === 'delete' ? 1 : 1
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {(state.modalType === 'edit' || state.modalType === 'view' || state.modalType === 'assign' || state.modalType === 'createEpic' || state.modalType === 'createStory' || state.modalType === 'createTask') && (
                            <Avatar
                                sx={{ borderRadius: '10px', bgcolor: '#f1f5f9', width: 45, height: 45 }}
                            >
                                {(() => {
                                    switch (state.modalType) {
                                        case 'edit':
                                            return <Edit color="primary" />;
                                        case 'assign':
                                            return <GroupAdd color="primary" />;
                                        case 'createEpic':
                                            return <PostAdd color="primary" />;
                                        case 'createStory':
                                            return <AutoAwesomeMotion color="primary" />;
                                        case 'createTask':
                                            return <AddTask color="primary" />;
                                        default:
                                            return (
                                                <Box
                                                    component="img"
                                                    src={projectIcon}
                                                    alt="project"
                                                    sx={{ width: 32, height: 32, objectFit: 'contain' }}
                                                />
                                            );
                                    }
                                })()}
                            </Avatar>
                        )}

                        <Box>
                            <Typography variant="h6" fontWeight="700">
                                {state.modalType === 'create' && 'Create New Project'}
                                {state.modalType === 'edit' && `Edit: ${state.selectedProject?.project_title}`}
                                {state.modalType === 'view' && state.selectedProject?.project_title}
                                {state.modalType === 'delete' && 'Confirm Delete'}
                                {state.modalType === 'assign' && 'Team Assignment'}
                                {state.modalType === 'createEpic' && 'Create New Epic'}
                                {state.modalType === 'createStory' && 'Create New Story'}
                                {state.modalType === 'createTask' && 'Create New Task'}
                            </Typography>

                            {(state.modalType === 'edit' || state.modalType === 'view') && (
                                <Typography variant="caption" fontWeight="500" color="text.secondary" sx={{ display: 'block' }}>
                                    Project ID: #{state.selectedProject?.id || 'N/A'}
                                </Typography>
                            )}

                            {(state.modalType === 'assign') && (
                                <Typography variant="caption" fontWeight="500" color="text.secondary" sx={{ display: 'block' }}>
                                    Manage resource allocation and project teams
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <IconButton onClick={() => handleCloseModal()} sx={{ color: (theme) => theme.palette.grey[500] }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ border: 'none', p: { xs: 1, sm: 3 } }}>
                    {state.modalType === 'create' && (
                        <CreateProject
                            onClose={() => handleCloseModal()}
                            onSuccess={() => handleCloseModal(undefined, undefined, true)}
                        />
                    )}

                    {state.modalType === 'view' && (
                        <ProjectViewPage project={state.selectedProject} />
                    )}

                    {state.modalType === 'edit' && (
                        <EditProjectPage
                            selectedProject={state.selectedProject}
                            onClose={() => handleCloseModal()}
                            onSuccess={() => handleCloseModal(undefined, undefined, true)}
                        />
                    )}

                    {state.modalType === 'assign' && (
                        <AssignProjectTeam
                            project={state.selectedProject}
                            projectId={state.selectedProject?.id}
                            onClose={() => handleCloseModal()}
                            onTeamAssignSuccess={() => handleCloseModal(undefined, undefined, true)}
                        />
                    )}

                    {state.modalType === 'createEpic' && (
                        <CreateEpicPage
                            projectData={state.selectedProject}
                            onClose={() => handleCloseModal()}
                            onSuccess={() => handleCloseModal(undefined, undefined, true)}
                        />
                    )}

                    {state.modalType === 'createStory' && (
                        <CreateUserStory
                            projectData={state.selectedProject}
                            onClose={() => handleCloseModal()}
                            onSuccess={() => handleCloseModal(undefined, undefined, true)}
                        />
                    )}

                    {state.modalType === 'createTask' && (
                        <CreateTask
                            activeProject={state.selectedProject}
                            onClose={() => handleCloseModal()}
                            onSuccess={() => handleCloseModal(undefined, undefined, true)}
                        />
                    )}

                    {state.modalType === 'delete' && (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                Are you sure you want to delete <strong>{state.selectedProject?.project_title}</strong>?
                            </Typography>
                            <Typography variant="caption">
                                This action cannot be undone and will remove all associated tasks.
                            </Typography>
                            <DialogActions sx={{ mt: 3, justifyContent: 'end' }}>
                                <Button onClick={() => handleCloseModal()} variant="outlined" sx={{ textTransform: 'none' }}>Cancel</Button>
                                <Button
                                    onClick={() => handleDeleteSubmit(state.selectedProject.id)}
                                    variant="contained"
                                    color="error"
                                    sx={{ textTransform: 'none' }}
                                >
                                    Delete Project
                                </Button>
                            </DialogActions>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box >
    );
};

export default ProjectsPage;